// Radio audio: pilot and controller voices through a simulated VHF receiver (band-pass 300–3000 Hz,
// compression/overdrive, carrier noise, squelch tail). Neural voices come from the Piper TTS service of
// atc.coone.dev (/api/tts, LibriTTS-R speakers, CC BY 4.0); without it the browser's speech synthesis is used.
import type { Transmission } from '../sim/commands';
import { hashStr } from '../sim/rng';

export interface AudioSettings { master: number; voices: boolean; ownVoice: boolean; natural: boolean; radioFx: boolean; sfx: boolean; otherFreqs: boolean }
const DEFAULTS: AudioSettings = { master: 0.8, voices: true, ownVoice: false, natural: true, radioFx: true, sfx: true, otherFreqs: false };
const KEY = 'frequenz_audio';

// Piper "en_US-libritts_r-medium" speakers chosen for clear, quick radio voices (same set as the previous game)
const PILOT_VOICES = [6, 18, 24, 30, 42, 57, 63, 72, 78, 81, 84, 87, 90, 93, 96, 102, 114, 117, 129, 132, 144, 159, 171, 177, 180, 183, 189, 192, 195, 198, 201, 204, 210, 216, 219, 222, 231, 243, 291, 294, 300, 303, 318, 327, 330, 333, 342, 393, 399, 405, 423, 429, 432, 435, 441, 456, 459, 465, 474, 477, 492, 495, 498, 504, 531, 537, 540, 543, 546, 549, 579, 603, 633, 636, 663, 690, 699, 705, 717, 723, 729, 744, 753, 762, 765, 768, 771, 789, 792, 795, 798, 804, 810, 813, 816, 825, 828, 831, 843, 858, 861, 876, 879, 885];
const ATC_VOICES = [288, 618, 264, 852, 171, 624];

function load(): AudioSettings {
  try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<AudioSettings>) }; } catch { return { ...DEFAULTS }; }
}

class RadioAudio {
  s: AudioSettings = load();
  private ctx: AudioContext | null = null;
  private out: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private queue: { tx: Transmission; audio: Promise<AudioBuffer | null> | null }[] = [];
  private playing = false;
  private naturalOk = true;
  private cache = new Map<string, Promise<AudioBuffer | null>>();
  private browserVoices: SpeechSynthesisVoice[] = [];
  private current: { stop: () => void } | null = null;

  set<K extends keyof AudioSettings>(k: K, v: AudioSettings[K]): void {
    this.s[k] = v;
    try { localStorage.setItem(KEY, JSON.stringify(this.s)); } catch { /* ignore */ }
    if (this.out) this.out.gain.value = this.s.master;
    if (k === 'natural' && v) this.naturalOk = true;
  }

  /** Must run after a user gesture (autoplay rules). */
  unlock(): void {
    if (this.ctx) { void this.ctx.resume(); return; }
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.out = this.ctx.createGain();
    this.out.gain.value = this.s.master;
    this.out.connect(this.ctx.destination);
    const len = this.ctx.sampleRate * 2;
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    if ('speechSynthesis' in window) {
      const pick = () => { this.browserVoices = speechSynthesis.getVoices().filter(v => v.lang.startsWith('en')); };
      pick(); speechSynthesis.onvoiceschanged = pick;
    }
  }

  stopAll(): void {
    this.queue = [];
    this.current?.stop();
    this.current = null;
    this.playing = false;
    if ('speechSynthesis' in window) speechSynthesis.cancel();
  }

  speaker(tx: Transmission): number {
    if (tx.from === 'ATC') return ATC_VOICES[hashStr(tx.voice) % ATC_VOICES.length]!;
    return PILOT_VOICES[hashStr(tx.voice) % PILOT_VOICES.length]!;
  }

  /** A transmission started on a frequency the player listens to. */
  play(tx: Transmission, timeScale: number): void {
    if (!this.ctx) return;
    const own = tx.from === 'ATC' && tx.byPlayer;
    const wanted = this.s.voices && (!own || this.s.ownVoice) && timeScale <= 2;
    if (!wanted) { if (this.s.sfx && !own) this.squelch(); return; }
    const audio = this.s.natural && this.naturalOk ? this.fetchVoice(tx.spoken, this.speaker(tx)) : null;
    this.queue.push({ tx, audio });
    while (this.queue.length > 3) this.queue.shift();
    if (!this.playing) void this.pump();
  }

  private fetchVoice(text: string, speaker: number): Promise<AudioBuffer | null> {
    const key = `${speaker}|${text}`;
    const hit = this.cache.get(key);
    if (hit) return hit;
    const p = fetch('/api/tts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, speaker }) })
      .then(async r => {
        if (!r.ok || !(r.headers.get('content-type') ?? '').includes('audio')) { if (r.status === 404 || r.status === 401 || !r.ok) this.naturalOk = r.status === 429 || r.status === 503; return null; }
        return this.ctx!.decodeAudioData(await r.arrayBuffer());
      })
      .catch(() => { this.naturalOk = false; return null; });
    this.cache.set(key, p);
    if (this.cache.size > 120) this.cache.delete(this.cache.keys().next().value!);
    return p;
  }

  private async pump(): Promise<void> {
    this.playing = true;
    while (this.queue.length) {
      const item = this.queue.shift()!;
      const buf = item.audio ? await Promise.race([item.audio, new Promise<null>(r => setTimeout(() => r(null), 4000))]) : null;
      if (buf) await this.playBuffer(buf, item.tx);
      else await this.speakBrowser(item.tx);
      await new Promise(r => setTimeout(r, 250));
    }
    this.playing = false;
  }

  private chain(tx: Transmission): { input: AudioNode; end: () => void } {
    const ctx = this.ctx!;
    const h = hashStr('rx' + tx.voice);
    const out = ctx.createGain();
    out.gain.value = tx.from === 'ATC' ? 0.9 : 1;
    out.connect(this.out!);
    if (!this.s.radioFx) return { input: out, end: () => {} };
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 280 + (h % 120);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2600 + ((h >>> 4) % 700);
    const shaper = ctx.createWaveShaper();
    const k = 1.5 + ((h >>> 8) % 25) / 10;
    const curve = new Float32Array(1024);
    for (let i = 0; i < 1024; i++) { const x = (i / 1023) * 2 - 1; curve[i] = Math.tanh(k * x) / Math.tanh(k); }
    shaper.curve = curve;
    const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -26; comp.ratio.value = 6;
    hp.connect(lp); lp.connect(shaper); shaper.connect(comp); comp.connect(out);
    // carrier noise while transmitting
    const noise = ctx.createBufferSource(); noise.buffer = this.noiseBuf; noise.loop = true;
    const ng = ctx.createGain(); ng.gain.value = tx.from === 'PILOT' ? 0.012 + ((h >>> 12) % 10) / 1000 : 0.006;
    const nf = ctx.createBiquadFilter(); nf.type = 'bandpass'; nf.frequency.value = 1800; nf.Q.value = 0.6;
    noise.connect(nf); nf.connect(ng); ng.connect(out); noise.start();
    return { input: hp, end: () => { try { noise.stop(); } catch { /* stopped */ } } };
  }

  private playBuffer(buf: AudioBuffer, tx: Transmission): Promise<void> {
    const ctx = this.ctx!;
    return new Promise(resolve => {
      if (this.s.sfx) this.click(true);
      const { input, end } = this.chain(tx);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = tx.from === 'PILOT' ? 1.05 : 1.08;
      src.connect(input);
      src.onended = () => { end(); if (this.s.sfx) this.squelch(); this.current = null; resolve(); };
      this.current = { stop: () => { try { src.stop(); } catch { /* stopped */ } } };
      src.start(ctx.currentTime + 0.08);
    });
  }

  private speakBrowser(tx: Transmission): Promise<void> {
    if (!('speechSynthesis' in window)) return Promise.resolve();
    return new Promise(resolve => {
      const u = new SpeechSynthesisUtterance(tx.spoken);
      const h = hashStr(tx.voice);
      const voices = this.browserVoices;
      if (voices.length) u.voice = voices[h % voices.length]!;
      u.lang = 'en-GB';
      u.rate = 1.12 + ((h >>> 3) % 10) / 100;
      u.pitch = 0.8 + ((h >>> 6) % 40) / 100;
      u.volume = this.s.master;
      if (this.s.sfx) this.click(true);
      u.onend = () => { if (this.s.sfx) this.squelch(); resolve(); };
      u.onerror = () => resolve();
      speechSynthesis.speak(u);
    });
  }

  /** Short press-to-talk click. */
  click(open: boolean): void {
    if (!this.ctx || !this.out) return;
    const ctx = this.ctx;
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = 'square'; o.frequency.value = open ? 1150 : 900;
    g.gain.setValueAtTime(0.025, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.035);
    o.connect(g); g.connect(this.out); o.start(); o.stop(ctx.currentTime + 0.04);
  }
  /** Squelch tail at the end of a transmission. */
  squelch(): void {
    if (!this.ctx || !this.out || !this.noiseBuf) return;
    const ctx = this.ctx;
    const n = ctx.createBufferSource(); n.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 2200; f.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.07, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.16);
    n.connect(f); f.connect(g); g.connect(this.out); n.start(); n.stop(ctx.currentTime + 0.18);
  }
  /** Alert tone (STCA, incidents). */
  alert(): void {
    if (!this.ctx || !this.out || !this.s.sfx) return;
    const ctx = this.ctx;
    for (let i = 0; i < 2; i++) {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 880; o.type = 'triangle';
      const t0 = ctx.currentTime + i * 0.22;
      g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(0.06, t0 + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
      o.connect(g); g.connect(this.out); o.start(t0); o.stop(t0 + 0.2);
    }
  }
}

export const Radio = new RadioAudio();
